/**
 * Google Places Service
 * Service commun pour toutes les recherches Google Places dans l'application
 *
 * Includes:
 * - Autocomplete (searchPlaces, searchAddresses)
 * - Place details (getPlaceDetails, getPlaceBasicInfo, getAddressComponents)
 * - Professional search for work requests prospection (searchProfessionals, getPlaceProspectDetails)
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

/**
 * Check if Google Places API is available
 * @returns {boolean}
 */
export const isGooglePlacesAvailable = () => {
    return !!(window.google?.maps?.places?.AutocompleteSuggestion);
};

/**
 * Wait for Google Places API to be loaded
 * @param {number} timeout - Timeout in ms (default 15000)
 * @returns {Promise<boolean>}
 */
export const waitForGooglePlaces = (timeout = 15000) => {
    return new Promise((resolve) => {
        if (isGooglePlacesAvailable()) {
            resolve(true);
            return;
        }

        const startTime = Date.now();
        const checkInterval = setInterval(() => {
            if (isGooglePlacesAvailable()) {
                clearInterval(checkInterval);
                resolve(true);
            } else if (Date.now() - startTime > timeout) {
                clearInterval(checkInterval);
                console.warn('Google Places API not loaded after timeout');
                resolve(false);
            }
        }, 100);
    });
};

/**
 * Build Google Maps URL from place ID
 * @param {string} placeId - Google Place ID
 * @returns {string}
 */
export const buildGoogleMapsUrl = (placeId) => {
    if (!placeId) return '';
    return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
};

/**
 * Search for places (establishments)
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {string[]} options.regionCodes - Region codes (default: ['be', 'fr', 'nl', 'de', 'lu'])
 * @param {string[]} options.types - Place types (default: ['establishment'])
 * @param {number} options.maxResults - Max results (default: 6)
 * @returns {Promise<Array<{placeId: string, name: string, description: string, secondaryText: string}>>}
 */
export const searchPlaces = async (query, options = {}) => {
    if (!query || query.length < 2) return [];

    if (!isGooglePlacesAvailable()) {
        console.warn('Google Places API not available');
        return [];
    }

    const {
        regionCodes = ['be', 'fr', 'nl', 'de', 'lu'],
        types = ['establishment'],
        maxResults = 6
    } = options;

    try {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedPrimaryTypes: types,
            includedRegionCodes: regionCodes
        });

        if (!suggestions || suggestions.length === 0) return [];

        return suggestions.slice(0, maxResults).map(suggestion => ({
            placeId: suggestion.placePrediction?.placeId || '',
            name: suggestion.placePrediction?.mainText?.text || '',
            description: suggestion.placePrediction?.text?.text || '',
            secondaryText: suggestion.placePrediction?.secondaryText?.text || ''
        }));
    } catch (error) {
        console.error('Error searching places:', error);
        return [];
    }
};

/**
 * Search for addresses (geocode)
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {string[]} options.regionCodes - Region codes (default: ['be', 'fr', 'nl', 'de', 'lu'])
 * @param {number} options.maxResults - Max results (default: 5)
 * @returns {Promise<Array<{placeId: string, description: string}>>}
 */
export const searchAddresses = async (query, options = {}) => {
    if (!query || query.length < 2) return [];

    if (!isGooglePlacesAvailable()) {
        console.warn('Google Places API not available');
        return [];
    }

    const {
        regionCodes = ['be', 'fr', 'nl', 'de', 'lu'],
        maxResults = 5
    } = options;

    try {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: regionCodes
        });

        if (!suggestions || suggestions.length === 0) return [];

        return suggestions.slice(0, maxResults).map(suggestion => ({
            placeId: suggestion.placePrediction?.placeId || '',
            description: suggestion.placePrediction?.text?.text || ''
        }));
    } catch (error) {
        console.error('Error searching addresses:', error);
        return [];
    }
};

/**
 * Get place details
 * @param {string} placeId - Google Place ID
 * @param {string[]} fields - Fields to fetch (default: basic fields)
 * @returns {Promise<Object|null>}
 */
export const getPlaceDetails = async (placeId, fields = null) => {
    if (!placeId) return null;

    if (!window.google?.maps?.places?.Place) {
        console.warn('Google Places API not available');
        return null;
    }

    const defaultFields = [
        'displayName',
        'formattedAddress',
        'location',
        'addressComponents',
        'googleMapsURI'
    ];

    try {
        const place = new window.google.maps.places.Place({ id: placeId });
        await place.fetchFields({ fields: fields || defaultFields });
        return place;
    } catch (error) {
        console.error('Error fetching place details:', error);
        return null;
    }
};

/**
 * Get basic place info for events/meetings/carpooling
 * Returns only placeName, googlePlaceId, googleUrl
 * @param {string} placeId - Google Place ID
 * @returns {Promise<{placeName: string, googlePlaceId: string, googleUrl: string}|null>}
 */
export const getPlaceBasicInfo = async (placeId) => {
    if (!placeId) return null;

    try {
        const place = await getPlaceDetails(placeId, ['displayName', 'formattedAddress', 'googleMapsURI']);

        if (!place) return null;

        const placeName = place.displayName || place.formattedAddress || '';
        const googleUrl = place.googleMapsURI || buildGoogleMapsUrl(placeId);

        return {
            placeName,
            googlePlaceId: placeId,
            googleUrl
        };
    } catch (error) {
        console.error('Error getting place basic info:', error);
        return null;
    }
};

/**
 * Get full address components from place
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object|null>}
 */
export const getAddressComponents = async (placeId) => {
    if (!placeId) return null;

    try {
        const place = await getPlaceDetails(placeId, [
            'displayName',
            'formattedAddress',
            'location',
            'addressComponents',
            'googleMapsURI'
        ]);

        if (!place) return null;

        const components = {
            googlePlaceId: placeId,
            googleUrl: place.googleMapsURI || buildGoogleMapsUrl(placeId),
            formattedAddress: place.formattedAddress || ''
        };

        // Extract coordinates
        if (place.location) {
            components.lat = place.location.lat();
            components.lng = place.location.lng();
        }

        // Parse address components
        if (place.addressComponents) {
            place.addressComponents.forEach(component => {
                const types = component.types;

                if (types.includes('street_number')) {
                    components.number = component.longText;
                }
                if (types.includes('route')) {
                    components.street = component.longText;
                }
                if (types.includes('postal_code')) {
                    components.postalCode = component.longText;
                }
                if (types.includes('locality')) {
                    components.city = component.longText;
                }
                if (types.includes('country')) {
                    components.country = component.longText;
                    components.countryCode = component.shortText;
                }
            });
        }

        return components;
    } catch (error) {
        console.error('Error getting address components:', error);
        return null;
    }
};

// ========================================================================
// Professional Search (Work Requests Prospection - Google Places Text Search)
// ========================================================================

/**
 * Extract city from address components array
 * @param {Array} addressComponents - Google Places address components
 * @returns {string} City name or empty string
 */
const extractCityFromComponents = (addressComponents) => {
    if (!addressComponents || !Array.isArray(addressComponents)) return '';
    for (const component of addressComponents) {
        const types = component.types || [];
        if (types.includes('locality')) {
            return component.longText || component.shortText || '';
        }
    }
    // Fallback: try sublocality or administrative_area_level_2
    for (const component of addressComponents) {
        const types = component.types || [];
        if (types.includes('sublocality') || types.includes('administrative_area_level_2')) {
            return component.longText || component.shortText || '';
        }
    }
    return '';
};

/**
 * Search for professionals by occupation and location using Google Places Text Search (New)
 * Used in the Prospection tab of Work Request dashboard.
 *
 * @param {string} query - Search query (e.g. "Électricien Bruxelles")
 * @param {{ lat: number, lng: number }} location - Center point for the search
 * @param {number} radius - Search radius in meters (default 5000)
 * @returns {Promise<Array<{ placeId: string, name: string, city: string, phone: string, website: string, rating: number, lat: number, lng: number }>>}
 */
export const searchProfessionals = async (query, location, radius = 5000) => {
    if (!query) return [];
    if (!GOOGLE_MAPS_API_KEY) {
        console.warn('Google Maps API key not configured');
        return [];
    }

    try {
        const body = {
            textQuery: query,
            languageCode: 'fr',
            maxResultCount: 20
        };

        // Add location bias if provided
        if (location && location.lat && location.lng) {
            body.locationBias = {
                circle: {
                    center: {
                        latitude: location.lat,
                        longitude: location.lng
                    },
                    radius: radius
                }
            };
        }

        const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
                'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.addressComponents,places.nationalPhoneNumber,places.internationalPhoneNumber,places.websiteUri,places.rating,places.location'
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            console.error('Google Places Text Search error:', response.status, await response.text());
            return [];
        }

        const data = await response.json();
        const places = data.places || [];

        return places.map(place => ({
            placeId: place.id || '',
            name: place.displayName?.text || '',
            // L'adresse complete, demandee au FieldMask et jusqu'ici jetee.
            // Le module Quartier en a besoin : « Multipharma, Mariembourg »
            // sans la rue ne sert a rien pour aller y chercher un medicament.
            address: place.formattedAddress || '',
            city: extractCityFromComponents(place.addressComponents),
            phone: place.internationalPhoneNumber || place.nationalPhoneNumber || '',
            website: place.websiteUri || '',
            rating: place.rating || 0,
            lat: place.location?.latitude || 0,
            lng: place.location?.longitude || 0
        }));
    } catch (error) {
        console.error('Error searching professionals:', error);
        return [];
    }
};

/**
 * Get detailed place information for prospection (phone, website, rating)
 * Extended version of getPlaceDetails for the prospection use case.
 *
 * @param {string} placeId - Google Place ID
 * @returns {Promise<{ name: string, address: string, city: string, phone: string, rating: number, website: string, lat: number, lng: number } | null>}
 */
export const getPlaceProspectDetails = async (placeId) => {
    if (!placeId) return null;
    if (!GOOGLE_MAPS_API_KEY) {
        console.warn('Google Maps API key not configured');
        return null;
    }

    try {
        const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
            headers: {
                'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
                'X-Goog-FieldMask': 'id,displayName,formattedAddress,addressComponents,nationalPhoneNumber,internationalPhoneNumber,websiteUri,rating,location'
            }
        });

        if (!response.ok) {
            console.error('Google Places Details error:', response.status);
            return null;
        }

        const place = await response.json();

        return {
            name: place.displayName?.text || '',
            address: place.formattedAddress || '',
            city: extractCityFromComponents(place.addressComponents),
            phone: place.internationalPhoneNumber || place.nationalPhoneNumber || '',
            rating: place.rating || 0,
            website: place.websiteUri || '',
            lat: place.location?.latitude || 0,
            lng: place.location?.longitude || 0
        };
    } catch (error) {
        console.error('Error fetching place prospect details:', error);
        return null;
    }
};

/**
 * Load Google Maps script (no-op — already loaded by APIProvider in App.jsx)
 * Kept for compatibility with the plan. Resolves when google.maps is available.
 *
 * @returns {Promise<boolean>}
 */
export const loadGoogleMapsScript = async () => {
    if (window.google?.maps) return true;
    return waitForGooglePlaces();
};
